import { forwardRef } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  type TamagoshiIconComponent,
  type TamagoshiLogoComponent,
} from '@/core/tamagoshiTailwind/interfaces';

export const LABEL_VARIANTS = [
  'AccentPrimary',
  'AccentSecondary',
  'BrandSecondary',
  'FoundationDisabled',
  'FoundationInverted',
  'FoundationPrimary',
  'FoundationQuinary',
  'FoundationSecondary',
  'LoyaltyPrimary',
  'LoyaltyTertiary',
  'MiscSecondary',
  'PromotionPrimary',
  'SuccessPrimary',
  'SustainablePrimary',
  'WarningPrimary',
] as const;

export type LabelVariant = (typeof LABEL_VARIANTS)[number];

const VARIANT_CLASSES: Record<LabelVariant, string> = {
  AccentPrimary: 'b2b bg-accent-primary text-foundation-primary',
  AccentSecondary: 'bg-accent-secondary text-foundation-primary',
  BrandSecondary: 'bg-brand-secondary text-foundation-primary',
  FoundationDisabled: 'bg-foundation-disabled text-foundation-primary',
  FoundationInverted: 'bg-foundation-inverted text-foundation-primary',
  FoundationPrimary: 'bg-foundation-primary text-foundation-primary',
  FoundationQuinary: 'bg-foundation-quinary text-foundation-inverted',
  FoundationSecondary: 'bg-foundation-primary text-foundation-secondary',
  LoyaltyPrimary: 'bg-loyalty-primary text-foundation-primary',
  LoyaltyTertiary: 'bg-loyalty-tertiary text-foundation-primary',
  MiscSecondary: 'bg-misc-secondary text-foundation-primary',
  PromotionPrimary: 'bg-promotion-primary text-foundation-inverted',
  SuccessPrimary: 'bg-success-primary text-foundation-primary',
  SustainablePrimary: 'bg-sustainable-primary text-foundation-inverted',
  WarningPrimary: 'bg-warning-primary text-foundation-primary',
};

type LabelPropsBase<T extends string | never> = {
  /** Variant to use */
  variant: LabelVariant;
  /** Text to display */
  text?: T;
};

export type LabelProps =
  | (Required<LabelPropsBase<string>> & {
      /** Icon to display */
      icon?: TamagoshiIconComponent;
      logo?: never;
    })
  | (LabelPropsBase<never> & {
      icon?: never;
      /** Logo to display */
      logo: TamagoshiLogoComponent;
    });

/**
 * Label component to display a tag with text and an optional icon.
 *
 * The style of the label is based on the provided `variant`. Logo-only labels
 * don't accept text.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15439-3556
 */
export const Label = forwardRef<HTMLSpanElement, LabelProps>(
  ({ variant, ...props }, ref) => {
    const [text, IconOrLogo] =
      'logo' in props ? [null, props.logo] : [props.text, props.icon ?? null];

    return (
      <span
        className={twJoin(
          `inline-block h-[20px] content-center space-x-xs truncate rounded-tag
          px-xs align-middle text-tiny font-bold leading-tiny desktop:text-small
          desktop:font-semibold desktop:leading-small`,
          // NOTE We know color class names are contained in this constant so we
          //      can get away with `twJoin` only.
          VARIANT_CLASSES[variant],
        )}
        ref={ref}
      >
        {!!text && <span>{text}</span>}
        {!!IconOrLogo && <IconOrLogo className="max-h-[16px] w-auto" />}
      </span>
    );
  },
);

Label.displayName = 'Label';
